import React, { useState, useEffect, useRef, useMemo } from 'react';
import { providersAPI } from '../../api/providers';
import { uploadImage } from '../../utils/cloudinary';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ClockIcon,
  PhotoIcon,
  ScissorsIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ListBulletIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  CurrencyDollarIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatter';
import { getCatalogForCategory, findCatalogService, ALL_CATALOG_SERVICES } from '../../data/serviceCatalog';
import toast from 'react-hot-toast';

// Generic add-on fallbacks — shown after the chosen service's own suggestions
const ADDON_SUGGESTIONS = [
  'Hair Treatment', 'Deep Conditioning', 'Scalp Massage', 'Hair Wash & Blow Dry',
  'Keratin Treatment', 'Hot Oil Treatment', 'Beard Oil Treatment', 'Beard Shaping',
  'Eyebrow Shaping', 'Eyebrow Tinting', 'Lash Tint', 'Face Mask',
  'Paraffin Wax Dip', 'Hand Massage', 'Foot Massage', 'Exfoliation',
  'Nail Polish Change', 'Gel Polish Removal', 'Nail Repair', 'Nail Extensions',
  'Hair Extensions', 'Colour Gloss', 'Highlights', 'Toner Application',
  'Aromatherapy', 'Hot Stones', 'Cupping', 'Steam Treatment',
  'Edge Control Styling', 'Protective Style Add-on', 'Extra Length', 'Rush Fee',
];

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120, 150, 180, 240];

// Format number with commas: 50000 -> "50,000"
const formatWithCommas = (value) => {
  const num = value.toString().replace(/[^0-9]/g, '');
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Remove commas for submission: "50,000" -> 50000
const removeCommas = (value) => parseInt(value.replace(/,/g, ''), 10) || 0;

// Active-service limits per subscription plan — mirrors the backend enforcement
const PLAN_SERVICE_LIMITS = { ORDINARY: 3, ADVANCED: 10, PREMIUM: null };

const ManageServices = () => {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [plan, setPlan] = useState('ORDINARY');
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  const [nameSearch, setNameSearch] = useState('');
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const nameRef = useRef(null);
  const [showAddonDropdown, setShowAddonDropdown] = useState(false);
  const addonNameRef = useRef(null);
  const [formData, setFormData] = useState({
    categoryId: '',
    name: '',
    description: '',
    price: '',
    duration: '',
    isMobile: false,
    isInShop: true,
    travelRadiusKm: '',
    travelFeePerKm: '',
    imageUrl: '',
  });
  const [addons, setAddons] = useState([]);
  const [newAddon, setNewAddon] = useState({
    name: '',
    description: '',
    price: '',
    durationExtra: '',
  });
  const [errors, setErrors] = useState({});
  const [addonErrors, setAddonErrors] = useState({});
  const [imageError, setImageError] = useState('');

  // Listing filters
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');
  const [togglingId, setTogglingId] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [search, activeTab, sort]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesData, categoriesData, profileData] = await Promise.all([
        providersAPI.getMyServices(),
        providersAPI.getCategories(),
        providersAPI.getMyProfile().catch(() => null),
      ]);
      setServices(servicesData || []);
      setCategories(categoriesData || []);
      const subscribedPlan = profileData?.subscription?.plan?.toUpperCase();
      if (subscribedPlan && PLAN_SERVICE_LIMITS[subscribedPlan] !== undefined) {
        setPlan(subscribedPlan);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'price' || name === 'travelFeePerKm') {
      // Format with commas as user types
      const raw = value.replace(/[^0-9]/g, '');
      setFormData({ ...formData, [name]: raw ? formatWithCommas(raw) : '' });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleAddonChange = (e) => {
    const { name, value } = e.target;
    if (name === 'price') {
      const raw = value.replace(/[^0-9]/g, '');
      setNewAddon({ ...newAddon, [name]: raw ? formatWithCommas(raw) : '' });
    } else {
      setNewAddon({ ...newAddon, [name]: value });
    }
    setAddonErrors(prev => ({ ...prev, [name]: '' }));
  };

  const addAddon = () => {
    const nextErrors = {};
    if (!newAddon.name.trim()) nextErrors.name = 'Add-on name is required';
    if (!removeCommas(newAddon.price)) nextErrors.price = 'Add-on price is required';
    setAddonErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    const addon = { ...newAddon, id: Date.now().toString() };
    setAddons(prev => [...prev, addon]);
    setNewAddon({ name: '', description: '', price: '', durationExtra: '' });
    toast.success('Add-on added — click Save to confirm');
  };

  const removeAddon = (id) => {
    setAddons(addons.filter((addon) => addon.id !== id));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError('');
    if (!file.type.startsWith('image/')) { setImageError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setImageError('Image must be under 5MB'); return; }

    setUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData(prev => ({ ...prev, imageUrl: url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const price = removeCommas(formData.price);
    const duration = parseInt(formData.duration);
    const nextErrors = {};
    if (!formData.categoryId) nextErrors.categoryId = 'Please select a category';
    if (!formData.name.trim()) nextErrors.name = 'Please enter a service name';
    if (!price || price < 0) nextErrors.price = 'Please enter a valid price';
    if (!duration || duration < 15 || duration > 480) nextErrors.duration = 'Duration must be 15 to 480 minutes';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setLoading(true);
    try {
      if (editingService) {
        // Update — only send fields the updateService schema accepts
        const updateData = {};
        if (formData.name.trim()) updateData.name = formData.name.trim();
        if (formData.description.trim()) updateData.description = formData.description.trim();
        updateData.price = price;
        updateData.duration = duration;
        updateData.isMobile = formData.isMobile;
        updateData.isInShop = formData.isInShop;
        if (formData.travelRadiusKm) updateData.travelRadiusKm = parseFloat(formData.travelRadiusKm);
        if (formData.travelFeePerKm) updateData.travelFeePerKm = removeCommas(formData.travelFeePerKm);
        if (formData.imageUrl) updateData.imageUrl = formData.imageUrl;
        await providersAPI.updateService(editingService.id, updateData);

        // Save addons for existing service
        for (const addon of addons) {
          if (addon.id && addon.id.includes && addon.id.includes('-')) continue;
          const addonData = { name: addon.name, price: removeCommas(addon.price), durationExtra: parseInt(addon.durationExtra || 0) };
          if (addon.description?.trim()) addonData.description = addon.description.trim();
          try {
            await providersAPI.createAddon(editingService.id, addonData);
          } catch (e) { console.error('Addon save error:', e); }
        }
        toast.success('Service updated successfully!');
      } else {
        // Create service
        const createData = {
          categoryId: formData.categoryId,
          name: formData.name.trim(),
          description: formData.description.trim(),
          price,
          duration,
        };
        if (formData.isMobile) createData.isMobile = true;
        if (!formData.isInShop) createData.isInShop = false;
        if (formData.travelRadiusKm) createData.travelRadiusKm = parseFloat(formData.travelRadiusKm);
        if (formData.travelFeePerKm) createData.travelFeePerKm = removeCommas(formData.travelFeePerKm);
        if (formData.imageUrl) createData.imageUrl = formData.imageUrl;
        const newService = await providersAPI.createService(createData);

        // Save addons for new service
        for (const addon of addons) {
          const addonData = { name: addon.name, price: removeCommas(addon.price), durationExtra: parseInt(addon.durationExtra || 0) };
          if (addon.description?.trim()) addonData.description = addon.description.trim();
          try {
            await providersAPI.createAddon(newService.id, addonData);
          } catch (e) { console.error('Addon save error:', e); }
        }
        toast.success('Service created successfully!');
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save service error:', error);
      if (error?.response?.data?.error === 'PLAN_LIMIT_REACHED') {
        toast.error(error.response.data.message || 'Service limit reached for your plan. Upgrade to add more.');
        setShowModal(false);
      } else {
        toast.error('Failed to save service');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      categoryId: service.categoryId,
      name: service.name,
      description: service.description,
      price: formatWithCommas(service.price),
      duration: service.duration.toString(),
      isMobile: service.isMobile,
      isInShop: service.isInShop,
      travelRadiusKm: service.travelRadiusKm?.toString() || '',
      travelFeePerKm: service.travelFeePerKm ? formatWithCommas(service.travelFeePerKm) : '',
      imageUrl: service.imageUrl || '',
    });
    setAddons(service.addons || []);
    setShowModal(true);
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await providersAPI.deleteService(serviceId);
      toast.success('Service deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleToggleActive = async (service) => {
    setTogglingId(service.id);
    try {
      await providersAPI.updateService(service.id, { isActive: !service.isActive });
      setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, isActive: !s.isActive } : s));
      toast.success(service.isActive ? 'Service paused' : 'Service activated');
    } catch (error) {
      console.error('Toggle active error:', error);
      toast.error('Failed to update service');
    } finally {
      setTogglingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      categoryId: '', name: '', description: '', price: '', duration: '',
      isMobile: false, isInShop: true, travelRadiusKm: '', travelFeePerKm: '', imageUrl: '',
    });
    setAddons([]);
    setEditingService(null);
    setErrors({});
    setAddonErrors({});
    setImageError('');
  };

  // `in` check, not ?? — PREMIUM's limit is null (unlimited), which ?? would
  // wrongly replace with the ORDINARY fallback
  const serviceLimit = plan in PLAN_SERVICE_LIMITS ? PLAN_SERVICE_LIMITS[plan] : PLAN_SERVICE_LIMITS.ORDINARY;
  const activeServiceCount = services.filter((s) => s.isActive).length;
  const atServiceLimit = serviceLimit !== null && activeServiceCount >= serviceLimit;

  // Catalog-driven suggestions: category → its services, service → its add-ons
  const selectedCategoryName = categories.find((c) => String(c.id) === String(formData.categoryId))?.name || null;
  const categoryCatalog = getCatalogForCategory(selectedCategoryName);
  const serviceSuggestions = (categoryCatalog || ALL_CATALOG_SERVICES).map((s) => s.name);
  const catalogService = findCatalogService(formData.name, selectedCategoryName);
  const addonSuggestions = [
    ...(catalogService?.addons || []),
    ...ADDON_SUGGESTIONS.filter((a) => !(catalogService?.addons || []).includes(a)),
  ];

  // Autofill description when a catalog service is picked — but never overwrite
  // text the provider typed themselves
  const pickCatalogService = (name) => {
    const entry = findCatalogService(name, selectedCategoryName);
    setFormData((prev) => {
      const descriptionUntouched =
        !prev.description || ALL_CATALOG_SERVICES.some((s) => s.description === prev.description);
      return {
        ...prev,
        name,
        description: entry && descriptionUntouched ? entry.description : prev.description,
      };
    });
    setErrors((prev) => ({ ...prev, name: '', description: '' }));
    setShowNameDropdown(false);
  };

  const handleAddNew = () => {
    if (atServiceLimit) {
      toast.error(`Your ${plan} plan allows up to ${serviceLimit} active services. Upgrade to add more.`);
      return;
    }
    resetForm();
    setShowModal(true);
  };

  // Listing filters / counts / sort
  const counts = useMemo(() => {
    const c = { all: services.length, active: 0, inactive: 0, mobile: 0, inShop: 0 };
    services.forEach((s) => {
      if (s.isActive) c.active++; else c.inactive++;
      if (s.isMobile) c.mobile++;
      if (s.isInShop) c.inShop++;
    });
    return c;
  }, [services]);

  const totalRevenue = useMemo(
    () => services.reduce((sum, s) => sum + (s.price || 0), 0),
    [services]
  );
  const avgPrice = services.length > 0 ? Math.round(totalRevenue / services.length) : 0;

  const categoryById = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.id] = c; });
    return m;
  }, [categories]);

  const filteredServices = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = services.filter((s) => {
      switch (activeTab) {
        case 'active':   if (!s.isActive) return false; break;
        case 'inactive': if (s.isActive) return false; break;
        case 'mobile':   if (!s.isMobile) return false; break;
        case 'inshop':   if (!s.isInShop) return false; break;
        default: break;
      }
      if (q) {
        const name = (s.name || '').toLowerCase();
        const desc = (s.description || '').toLowerCase();
        const cat = (categoryById[s.categoryId]?.name || '').toLowerCase();
        if (!name.includes(q) && !desc.includes(q) && !cat.includes(q)) return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (sort) {
        case 'price-high': return (b.price || 0) - (a.price || 0);
        case 'price-low':  return (a.price || 0) - (b.price || 0);
        case 'duration':   return (a.duration || 0) - (b.duration || 0);
        case 'name':       return (a.name || '').localeCompare(b.name || '');
        case 'oldest':     return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        default:           return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      }
    });
    return list;
  }, [services, activeTab, search, sort, categoryById]);

  const tabs = [
    { id: 'all',      label: 'All',       icon: ListBulletIcon,            count: counts.all },
    { id: 'active',   label: 'Active',    icon: CheckCircleIcon,           count: counts.active },
    { id: 'inactive', label: 'Inactive',  icon: PauseCircleIcon,           count: counts.inactive },
    { id: 'mobile',   label: 'Mobile',    icon: TruckIcon,                 count: counts.mobile },
    { id: 'inshop',   label: 'In-Shop',   icon: BuildingStorefrontIcon,    count: counts.inShop },
  ];

  const hasActiveFilters = search || activeTab !== 'all' || sort !== 'newest';

  if (loading && services.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px' }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Manage Services</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Build, price, and showcase the services you offer
          </p>
        </div>

        {/* Summary banner — compact one-liner */}
        <div
          className="flex items-center gap-2.5 mb-6 rounded-xl"
          style={{
            background: 'linear-gradient(135deg, var(--orange-2), #fff)',
            border: '1px solid var(--orange-5)',
            padding: '10px 14px',
          }}
        >
          <div
            style={{
              width: '28px', height: '28px', borderRadius: '8px',
              backgroundColor: 'var(--orange-9)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <RectangleStackIcon style={{ width: '15px', height: '15px' }} />
          </div>
          <p
            className="flex-1 min-w-0 truncate"
            style={{ fontSize: 'clamp(12px, 3vw, 14px)', color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}
            title={serviceLimit === null ? 'Unlimited services on your plan' : `${activeServiceCount} of ${serviceLimit} active services used`}
          >
            <span style={{ fontWeight: 700 }}>{counts.all}</span> service{counts.all !== 1 ? 's' : ''}
            {counts.active > 0 && <> · <span style={{ fontWeight: 700 }}>{counts.active}</span> active</>}
            {avgPrice > 0 && <span style={{ color: 'var(--gray-11)' }}> · avg {formatCurrency(avgPrice)}</span>}
            <span style={{ color: atServiceLimit ? 'var(--red-10, #d93d42)' : 'var(--gray-11)' }}>
              {' '}· {serviceLimit === null
                ? `${plan.charAt(0)}${plan.slice(1).toLowerCase()} · unlimited`
                : `${plan.charAt(0)}${plan.slice(1).toLowerCase()} · ${activeServiceCount}/${serviceLimit} used${atServiceLimit ? ' — upgrade' : ''}`}
            </span>
          </p>
          <Button onClick={handleAddNew} size="small" disabled={atServiceLimit} title={atServiceLimit ? `Your ${plan} plan limit is ${serviceLimit} active services` : undefined}>
            <PlusIcon style={{ width: '14px', height: '14px' }} />
            Add Service
          </Button>
        </div>

        {/* Tabs */}
        {services.length > 0 && (
          <div
            className="flex gap-1 mb-6 overflow-x-auto"
            style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
          >
            {tabs.map(({ id, label, icon: Icon, count }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 16px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: activeTab === id ? 600 : 400,
                  color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                  borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon style={{ width: '16px', height: '16px' }} />
                {label}
                {count > 0 && (
                  <span style={{
                    fontSize: '11px', fontWeight: 600,
                    padding: '1px 7px', borderRadius: '999px',
                    backgroundColor: activeTab === id ? 'var(--orange-3)' : 'var(--gray-4)',
                    color: activeTab === id ? 'var(--orange-11)' : 'var(--gray-11)',
                  }}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Empty state — no services at all */}
        {services.length === 0 ? (
          <div
            className="rounded-xl text-center py-16"
            style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
          >
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              backgroundColor: 'var(--orange-3)', margin: '0 auto 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ScissorsIcon style={{ width: '30px', height: '30px', color: 'var(--orange-9)' }} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 6px' }}>
              No services yet
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: '0 0 18px', textAlign: 'center' }}>
              Add your first service so clients can book you.
            </p>
            <Button onClick={handleAddNew}>
              <PlusIcon style={{ width: '16px', height: '16px' }} />
              Add Your First Service
            </Button>
          </div>
        ) : (
          <>
            {/* Search + sort row */}
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <div style={{ position: 'relative', flex: 1, minWidth: '240px' }}>
                <MagnifyingGlassIcon style={{
                  position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                  width: '16px', height: '16px', color: 'var(--gray-9)', pointerEvents: 'none',
                }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by service name, description, or category..."
                  style={{
                    width: '100%', padding: '10px 36px 10px 38px', borderRadius: '10px',
                    border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                  }}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    style={{
                      position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                      padding: '4px', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex',
                    }}
                  >
                    <XMarkIcon style={{ width: '16px', height: '16px', color: 'var(--gray-9)' }} />
                  </button>
                )}
              </div>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  padding: '9px 12px', borderRadius: '10px',
                  border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                  color: 'var(--gray-12)', fontSize: '13px', cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name">Name (A–Z)</option>
                <option value="price-high">Price (high → low)</option>
                <option value="price-low">Price (low → high)</option>
                <option value="duration">Duration (short → long)</option>
              </select>
            </div>

            {/* Filtered result count */}
            <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '0 0 12px', textAlign: 'left' }}>
              Showing {filteredServices.length} of {services.length}
              {hasActiveFilters && ` (filtered)`}
            </p>

            {filteredServices.length === 0 ? (
              <div
                className="rounded-xl text-center py-12"
                style={{ backgroundColor: 'var(--color-background)', border: '1px dashed var(--gray-5)' }}
              >
                <p style={{ fontSize: '14px', color: 'var(--gray-10)', margin: 0 }}>
                  No services match your filters.
                </p>
                <button
                  type="button"
                  onClick={() => { setSearch(''); setActiveTab('all'); setSort('newest'); }}
                  style={{
                    marginTop: '8px', padding: '6px 14px', borderRadius: '8px',
                    border: '1px solid var(--orange-6)', backgroundColor: 'transparent',
                    color: 'var(--orange-9)', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Reset filters
                </button>
              </div>
            ) : (
              <>
                {/* Pagination summary */}
                {(() => {
                  const totalPages = Math.max(1, Math.ceil(filteredServices.length / PER_PAGE));
                  const safePage = Math.min(page, totalPages);
                  const pageItems = filteredServices.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

                  return (
                    <>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pageItems.map((service) => {
                          const cat = categoryById[service.categoryId];
                          const addonCount = service.addons?.length || 0;
                          const toggling = togglingId === service.id;
                          return (
                            <li
                              key={service.id}
                              className="rounded-lg"
                              style={{
                                padding: '12px',
                                backgroundColor: 'var(--color-background)',
                                border: '1px solid var(--gray-4)',
                                opacity: service.isActive ? 1 : 0.7,
                                display: 'flex', alignItems: 'center', gap: '14px',
                                transition: 'background-color 0.15s ease',
                              }}
                            >
                              {/* Thumbnail */}
                              <div style={{
                                width: '64px', height: '64px', borderRadius: '8px',
                                flexShrink: 0,
                                backgroundColor: 'var(--gray-3)',
                                backgroundImage: service.imageUrl ? `url(${service.imageUrl})` : 'none',
                                backgroundSize: 'cover', backgroundPosition: 'center',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {!service.imageUrl && (
                                  <PhotoIcon style={{ width: '24px', height: '24px', color: 'var(--gray-7)' }} />
                                )}
                              </div>

                              {/* Body */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div className="flex items-center gap-2" style={{ marginBottom: '4px' }}>
                                  <p style={{
                                    fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)',
                                    margin: 0, textAlign: 'left',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {service.name}
                                  </p>
                                  <span style={{
                                    padding: '2px 8px', borderRadius: '999px',
                                    fontSize: '10px', fontWeight: 600, flexShrink: 0,
                                    backgroundColor: service.isActive ? 'var(--green-3, #dcfce7)' : 'var(--gray-4)',
                                    color: service.isActive ? '#15803d' : 'var(--gray-11)',
                                  }}>
                                    {service.isActive ? 'Active' : 'Paused'}
                                  </span>
                                </div>

                                {service.description && (
                                  <p style={{
                                    fontSize: '12px', color: 'var(--gray-10)',
                                    margin: '0 0 6px', textAlign: 'left',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {service.description}
                                  </p>
                                )}

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                  {cat?.name && (
                                    <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>
                                      {cat.name}
                                    </span>
                                  )}
                                  <span style={{
                                    fontSize: '12px', fontWeight: 700, color: 'var(--orange-9)',
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                  }}>
                                    <CurrencyDollarIcon style={{ width: '12px', height: '12px' }} />
                                    {formatCurrency(service.price)}
                                  </span>
                                  <span style={{
                                    fontSize: '12px', color: 'var(--gray-10)',
                                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                                  }}>
                                    <ClockIcon style={{ width: '12px', height: '12px' }} />
                                    {service.duration} min
                                  </span>
                                  {service.isInShop && (
                                    <span style={{
                                      padding: '1px 7px', borderRadius: '999px',
                                      fontSize: '10px', fontWeight: 600,
                                      backgroundColor: 'var(--blue-3, #dbeafe)', color: '#1d4ed8',
                                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    }}>
                                      <BuildingStorefrontIcon style={{ width: '10px', height: '10px' }} />
                                      In-shop
                                    </span>
                                  )}
                                  {service.isMobile && (
                                    <span style={{
                                      padding: '1px 7px', borderRadius: '999px',
                                      fontSize: '10px', fontWeight: 600,
                                      backgroundColor: 'var(--orange-3)', color: 'var(--orange-11)',
                                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                                    }}>
                                      <TruckIcon style={{ width: '10px', height: '10px' }} />
                                      Mobile{service.travelRadiusKm ? ` · ${service.travelRadiusKm}km` : ''}
                                    </span>
                                  )}
                                  {addonCount > 0 && (
                                    <span style={{
                                      padding: '1px 7px', borderRadius: '999px',
                                      fontSize: '10px', fontWeight: 600,
                                      backgroundColor: 'var(--gray-3)', color: 'var(--gray-11)',
                                    }}>
                                      +{addonCount} add-on{addonCount !== 1 ? 's' : ''}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                                <button
                                  type="button"
                                  onClick={() => handleEdit(service)}
                                  title="Edit service"
                                  style={{
                                    padding: '8px 10px', borderRadius: '8px',
                                    border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                                    color: 'var(--gray-12)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  }}
                                >
                                  <PencilIcon style={{ width: '14px', height: '14px' }} />
                                  <span className="hidden sm:inline">Edit</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleToggleActive(service)}
                                  disabled={toggling}
                                  title={service.isActive ? 'Pause this service' : 'Activate this service'}
                                  style={{
                                    padding: '8px', borderRadius: '8px',
                                    border: '1px solid var(--gray-5)', backgroundColor: 'transparent',
                                    color: service.isActive ? 'var(--gray-11)' : 'var(--orange-9)',
                                    cursor: toggling ? 'wait' : 'pointer',
                                    opacity: toggling ? 0.6 : 1,
                                    display: 'inline-flex', alignItems: 'center',
                                  }}
                                >
                                  {service.isActive
                                    ? <PauseCircleIcon style={{ width: '16px', height: '16px' }} />
                                    : <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                                  }
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(service.id)}
                                  title="Delete service"
                                  style={{
                                    padding: '8px', borderRadius: '8px',
                                    border: '1px solid var(--red-6, #fca5a5)', backgroundColor: 'transparent',
                                    color: 'var(--red-9, #e5484d)', cursor: 'pointer',
                                    display: 'inline-flex', alignItems: 'center',
                                  }}
                                >
                                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>

                      {/* Pagination */}
                      {filteredServices.length > PER_PAGE && (
                        <div
                          className="flex items-center justify-between mt-5 pt-4"
                          style={{ borderTop: '1px solid var(--gray-4)' }}
                        >
                          <span style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                            Page {safePage} of {totalPages}
                          </span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={safePage === 1}
                              style={{
                                padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                                cursor: safePage === 1 ? 'default' : 'pointer',
                                backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                                opacity: safePage === 1 ? 0.4 : 1,
                              }}
                            >
                              Prev
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                              .reduce((acc, p, i, arr) => {
                                if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                acc.push(p);
                                return acc;
                              }, [])
                              .map((p, i) => p === '...' ? (
                                <span key={`gap-${i}`} style={{ padding: '0 6px', color: 'var(--gray-9)', fontSize: '13px', alignSelf: 'center' }}>…</span>
                              ) : (
                                <button
                                  key={p}
                                  onClick={() => setPage(p)}
                                  style={{
                                    width: '32px', height: '32px', borderRadius: '6px', border: 'none',
                                    fontSize: '13px', fontWeight: safePage === p ? 600 : 400, cursor: 'pointer',
                                    backgroundColor: safePage === p ? 'var(--orange-9)' : 'var(--gray-4)',
                                    color: safePage === p ? '#fff' : 'var(--gray-11)',
                                  }}
                                >
                                  {p}
                                </button>
                              ))}
                            <button
                              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                              disabled={safePage === totalPages}
                              style={{
                                padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '13px',
                                cursor: safePage === totalPages ? 'default' : 'pointer',
                                backgroundColor: 'var(--gray-4)', color: 'var(--gray-11)',
                                opacity: safePage === totalPages ? 0.4 : 1,
                              }}
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingService ? 'Edit Service' : 'Add New Service'}
        size="large"
      >
        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <Select
            label="Category"
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            options={categories.map((cat) => ({ value: cat.id, label: cat.name }))}
            error={errors.categoryId}
            required
          />

          {/* Service Name — searchable dropdown */}
          <div style={{ position: 'relative' }} ref={nameRef}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>
              Service Name <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                setErrors(prev => ({ ...prev, name: '' }));
                setNameSearch(e.target.value);
                setShowNameDropdown(true);
              }}
              onFocus={() => setShowNameDropdown(true)}
              placeholder="Search or type a service name..."
              required
              style={{
                width: '100%', padding: '8px 12px', borderRadius: '6px',
                border: `1px solid ${errors.name ? 'var(--red-9, #e5484d)' : 'var(--gray-7)'}`, backgroundColor: 'var(--color-background)',
                color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
              }}
              onFocusCapture={e => { e.target.style.borderColor = 'var(--orange-9)'; e.target.style.boxShadow = '0 0 0 2px var(--orange-a4)'; }}
              onBlurCapture={e => {
                setTimeout(() => setShowNameDropdown(false), 200);
                e.target.style.borderColor = 'var(--gray-7)';
                e.target.style.boxShadow = 'none';
              }}
            />
            {errors.name && (
              <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', margin: '4px 0 0', textAlign: 'left' }}>
                {errors.name}
              </p>
            )}
            {showNameDropdown && (
              <ul style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                maxHeight: '200px', overflowY: 'auto',
                backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-6)',
                borderRadius: '8px', marginTop: '4px', padding: '4px 0',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)', listStyle: 'none',
              }}>
                {selectedCategoryName && categoryCatalog && (
                  <li style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: 'var(--orange-10)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {selectedCategoryName} services
                  </li>
                )}
                {serviceSuggestions
                  .filter(s => s.toLowerCase().includes((nameSearch || '').toLowerCase()))
                  .map(name => (
                    <li
                      key={name}
                      onMouseDown={() => pickCatalogService(name)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '14px',
                        color: 'var(--gray-12)', transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--orange-3)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {name}
                    </li>
                  ))
                }
                {serviceSuggestions.filter(s => s.toLowerCase().includes((nameSearch || '').toLowerCase())).length === 0 && (
                  <li style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--gray-9)' }}>
                    No suggestions — type your own name
                  </li>
                )}
              </ul>
            )}
          </div>

          <Textarea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Describe your service..."
            rows={3}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            {/* Price with comma formatting */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>
                Price (UGX) <span style={{ color: 'var(--red-9, #e5484d)' }}>*</span>
              </label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder="e.g. 50,000"
                required
                inputMode="numeric"
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: '6px',
                  border: `1px solid ${errors.price ? 'var(--red-9, #e5484d)' : 'var(--gray-7)'}`, backgroundColor: 'var(--color-background)',
                  color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                }}
                onFocus={e => { e.target.style.borderColor = 'var(--orange-9)'; e.target.style.boxShadow = '0 0 0 2px var(--orange-a4)'; }}
                onBlur={e => { e.target.style.borderColor = 'var(--gray-7)'; e.target.style.boxShadow = 'none'; }}
              />
              {formData.price && (
                <p style={{ fontSize: '12px', color: 'var(--gray-9)', marginTop: '4px', textAlign: 'left' }}>
                  UGX {formatWithCommas(formData.price.replace(/,/g, ''))}
                </p>
              )}
              {errors.price && (
                <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', marginTop: '4px', textAlign: 'left' }}>
                  {errors.price}
                </p>
              )}
            </div>

            {/* Duration with presets */}
            <Select
              label="Duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              options={DURATION_PRESETS.map(d => ({
                value: d.toString(),
                label: d >= 60 ? `${Math.floor(d / 60)}h ${d % 60 ? d % 60 + 'min' : ''}`.trim() : `${d} min`,
              }))}
              placeholder="Select duration"
              error={errors.duration}
              required
            />
          </div>

          {/* Service Image Upload */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '8px', textAlign: 'left' }}>
              Service Image (optional)
            </label>
            {formData.imageUrl && (
              <div style={{ marginBottom: '8px', position: 'relative', display: 'inline-block' }}>
                <img src={formData.imageUrl} alt="Service" style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                  style={{
                    position: 'absolute', top: '-6px', right: '-6px',
                    width: '22px', height: '22px', borderRadius: '50%',
                    backgroundColor: 'var(--red-9, #e5484d)', border: 'none',
                    color: '#fff', fontSize: '12px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '8px',
                border: '1px solid var(--gray-6)', backgroundColor: 'transparent',
                color: 'var(--gray-11)', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
              }}
            >
              <PhotoIcon style={{ width: '16px', height: '16px' }} />
              {uploading ? 'Uploading...' : formData.imageUrl ? 'Change Image' : 'Upload Image'}
            </button>
            {imageError && (
              <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', margin: '6px 0 0', textAlign: 'left' }}>
                {imageError}
              </p>
            )}
          </div>

          {/* Service Type */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '8px', textAlign: 'left' }}>
              Service Location
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" name="isInShop" checked={formData.isInShop} onChange={handleChange}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--orange-9)' }} />
                <span style={{ color: 'var(--gray-12)', fontSize: '14px' }}>Offer at my location (In-Shop)</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" name="isMobile" checked={formData.isMobile} onChange={handleChange}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--orange-9)' }} />
                <span style={{ color: 'var(--gray-12)', fontSize: '14px' }}>Offer at client's location (Mobile)</span>
              </label>
            </div>
          </div>

          {/* Travel Options */}
          {formData.isMobile && (
            <div className="p-4 rounded-lg space-y-4" style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-4)' }}>
              <h4 style={{ fontWeight: 500, color: 'var(--gray-12)', fontSize: '14px', textAlign: 'left' }}>Mobile Service Options</h4>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Max Travel Distance (km)" type="number" name="travelRadiusKm"
                  value={formData.travelRadiusKm} onChange={handleChange} placeholder="10" />
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>
                    Travel Fee per km (UGX)
                  </label>
                  <input type="text" name="travelFeePerKm" value={formData.travelFeePerKm} onChange={handleChange}
                    placeholder="e.g. 1,000" inputMode="numeric"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid var(--gray-7)', backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                    }} />
                </div>
              </div>
            </div>
          )}

          {/* Add-ons Section */}
          <div style={{ borderTop: '1px solid var(--gray-4)', paddingTop: '20px' }}>
            <h4 style={{ fontWeight: 500, color: 'var(--gray-12)', fontSize: '14px', marginBottom: '12px', textAlign: 'left' }}>
              Add-ons (Optional)
            </h4>

            {addons.length > 0 && (
              <div className="space-y-2 mb-4">
                {addons.map((addon) => (
                  <div key={addon.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--gray-3)' }}>
                    <div>
                      <p className="font-medium" style={{ color: 'var(--gray-12)', fontSize: '14px', textAlign: 'left' }}>{addon.name}</p>
                      <p className="text-sm" style={{ color: 'var(--gray-10)', textAlign: 'left' }}>
                        UGX {formatWithCommas(addon.price.toString().replace(/,/g, ''))}
                        {addon.durationExtra > 0 && ` · +${addon.durationExtra} min`}
                      </p>
                    </div>
                    <button type="button" onClick={() => removeAddon(addon.id)} style={{ color: 'var(--red-9, #e5484d)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-3 p-4 rounded-lg" style={{ backgroundColor: 'var(--gray-3)' }}>
              <div style={{ position: 'relative' }} ref={addonNameRef}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>
                  Add-on Name
                </label>
                <input
                  type="text"
                  value={newAddon.name}
                  onChange={(e) => {
                    setNewAddon({ ...newAddon, name: e.target.value });
                    setAddonErrors(prev => ({ ...prev, name: '' }));
                    setShowAddonDropdown(true);
                  }}
                  onFocus={() => setShowAddonDropdown(true)}
                  placeholder="Search or type an add-on name..."
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '6px',
                    border: `1px solid ${addonErrors.name ? 'var(--red-9, #e5484d)' : 'var(--gray-7)'}`, backgroundColor: 'var(--color-background)',
                    color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                  }}
                  onFocusCapture={e => { e.target.style.borderColor = 'var(--orange-9)'; e.target.style.boxShadow = '0 0 0 2px var(--orange-a4)'; }}
                  onBlurCapture={e => {
                    setTimeout(() => setShowAddonDropdown(false), 200);
                    e.target.style.borderColor = 'var(--gray-7)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {addonErrors.name && (
                  <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', margin: '4px 0 0', textAlign: 'left' }}>
                    {addonErrors.name}
                  </p>
                )}
                {showAddonDropdown && (
                  <ul style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    maxHeight: '180px', overflowY: 'auto',
                    backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-6)',
                    borderRadius: '8px', marginTop: '4px', padding: '4px 0',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)', listStyle: 'none',
                  }}>
                    {catalogService?.addons?.length > 0 && (
                      <li style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: 'var(--orange-10)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Suggested for {catalogService.name}
                      </li>
                    )}
                    {addonSuggestions
                      .filter(s => s.toLowerCase().includes((newAddon.name || '').toLowerCase()))
                      .map(name => (
                        <li
                          key={name}
                          onMouseDown={() => {
                            setNewAddon(prev => ({ ...prev, name }));
                            setAddonErrors(prev => ({ ...prev, name: '' }));
                            setShowAddonDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px', cursor: 'pointer', fontSize: '14px',
                            color: 'var(--gray-12)', transition: 'background 0.1s',
                            fontWeight: catalogService?.addons?.includes(name) ? 600 : 400,
                          }}
                          onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--orange-3)'}
                          onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {name}
                        </li>
                      ))
                    }
                    {addonSuggestions.filter(s => s.toLowerCase().includes((newAddon.name || '').toLowerCase())).length === 0 && (
                      <li style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--gray-9)' }}>
                        No suggestions — type your own
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <Textarea label="Description" name="description" value={newAddon.description} onChange={handleAddonChange} placeholder="Optional description..." rows={2} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '4px', textAlign: 'left' }}>Price (UGX)</label>
                  <input type="text" name="price" value={newAddon.price} onChange={handleAddonChange}
                    placeholder="e.g. 15,000" inputMode="numeric"
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: `1px solid ${addonErrors.price ? 'var(--red-9, #e5484d)' : 'var(--gray-7)'}`, backgroundColor: 'var(--color-background)',
                      color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                    }} />
                  {addonErrors.price && (
                    <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', margin: '4px 0 0', textAlign: 'left' }}>
                      {addonErrors.price}
                    </p>
                  )}
                </div>
                <Input label="Extra Time (min)" type="number" name="durationExtra" value={newAddon.durationExtra} onChange={handleAddonChange} placeholder="15" />
              </div>
              <Button type="button" variant="outline" fullWidth onClick={addAddon} size="small">
                <PlusIcon className="w-4 h-4" /> Add This Add-on
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" size="small" onClick={() => { setShowModal(false); resetForm(); }} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" size="small" loading={loading}>
              {editingService ? 'Update' : 'Create Service'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageServices;
